import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Plus, Edit2, Trash2, Bot, Loader2, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { useSimulationStore } from '@/store/simulationStore'
import { SimulationBot } from '@/services/simulation.service'
import { MarkdownEditor } from '@/components/common/MarkdownEditor'

interface BotFormData {
  name: string
  description: string
  guidance: string
  provider: string
  model: string
  temperature: number
  max_tokens: number
  status: 'active' | 'inactive'
}

const defaultFormData: BotFormData = {
  name: '',
  description: '',
  guidance: '',
  provider: 'openai',
  model: 'gpt-4o-mini',
  temperature: 0.8,
  max_tokens: 512,
  status: 'active',
}

export function BotsPage() {
  const { id: botIdFromUrl } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { bots, botsLoading, fetchBots, createBot, updateBot, deleteBot } = useSimulationStore()

  const [showModal, setShowModal] = useState(false)
  const [editingBot, setEditingBot] = useState<SimulationBot | null>(null)
  const [formData, setFormData] = useState<BotFormData>(defaultFormData)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    fetchBots().catch(() => {
      toast.error('Failed to load bots')
    })
  }, [fetchBots])

  useEffect(() => {
    if (!botIdFromUrl || botsLoading) return

    if (botIdFromUrl === 'new') {
      openCreateModal()
    } else {
      const bot = bots.find((b) => b.id === botIdFromUrl)
      if (bot) {
        openEditModal(bot)
      }
    }
  }, [botIdFromUrl, bots, botsLoading])

  const openCreateModal = () => {
    setEditingBot(null)
    setFormData(defaultFormData)
    setShowModal(true)
    navigate('/bots/new')
  }

  const openEditModal = (bot: SimulationBot) => {
    setEditingBot(bot)
    setFormData({
      name: bot.name,
      description: bot.description || '',
      guidance: bot.guidance || '',
      provider: bot.ai_model?.provider || 'openai',
      model: bot.ai_model?.model || 'gpt-4o-mini',
      temperature: bot.ai_model?.temperature ?? 0.8,
      max_tokens: bot.ai_model?.max_tokens ?? 512,
      status: bot.status,
    })
    setShowModal(true)
    navigate('/bots/' + bot.id)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingBot(null)
    setFormData(defaultFormData)
    navigate('/bots')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      toast.error('Bot name is required')
      return
    }

    setIsSaving(true)
    try {
      const payload: Partial<SimulationBot> = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        guidance: formData.guidance.trim(),
        ai_model: {
          provider: formData.provider,
          model: formData.model.trim(),
          temperature: formData.temperature,
          max_tokens: formData.max_tokens,
        },
        status: formData.status,
      }

      if (editingBot) {
        await updateBot(editingBot.id, payload)
        toast.success('Bot updated successfully')
      } else {
        await createBot(payload)
        toast.success('Bot created successfully')
      }
      closeModal()
    } catch {
      toast.error(editingBot ? 'Failed to update bot' : 'Failed to create bot')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (bot: SimulationBot) => {
    if (!confirm(`Delete "${bot.name}"? This action cannot be undone.`)) {
      return
    }

    try {
      await deleteBot(bot.id)
      toast.success('Bot deleted successfully')
    } catch {
      toast.error('Failed to delete bot')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Simulation Bots</h1>
          <p className="text-gray-500 mt-1">
            Create and manage bots for agent simulation testing
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-rose-500 text-white font-semibold rounded-xl hover:bg-rose-600 transition-colors shadow-[3px_3px_0px_#1a1a2e]"
        >
          <Plus className="w-4 h-4" />
          Create Bot
        </button>
      </div>

      {/* Loading State */}
      {botsLoading ? (
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-rose-600 animate-spin" />
        </div>
      ) : bots.length === 0 ? (
        /* Empty State */
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12">
          <div className="text-center max-w-md mx-auto">
            <div className="w-16 h-16 rounded-xl bg-gray-50 flex items-center justify-center mx-auto mb-6">
              <Bot className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No simulation bots yet
            </h3>
            <p className="text-gray-500 mb-6">
              Create your first bot to start simulating customer conversations with your agents.
            </p>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-rose-500 text-white font-semibold rounded-xl hover:bg-rose-600 transition-colors shadow-[3px_3px_0px_#1a1a2e]"
            >
              <Plus className="w-4 h-4" />
              Create Bot
            </button>
          </div>
        </div>
      ) : (
        /* Bot Table */
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1a1a2e] bg-gray-50">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Model</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Temperature</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {bots.map((bot) => (
                <tr
                  key={bot.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-gray-900">{bot.name}</p>
                    <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">
                      {bot.description || 'No description'}
                    </p>
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-sm text-gray-900">
                      {bot.ai_model?.model || 'gpt-4o-mini'}
                    </p>
                    <p className="text-xs text-gray-500 capitalize mt-0.5">
                      {bot.ai_model?.provider || 'openai'}
                    </p>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">
                    {bot.ai_model?.temperature ?? 0.8}
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        bot.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          bot.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                        }`}
                      />
                      {bot.status.charAt(0).toUpperCase() + bot.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-500">
                    {new Date(bot.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEditModal(bot)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(bot)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={closeModal} />
          <div className="relative bg-white rounded-xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-500 rounded-xl flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  {editingBot ? 'Edit Bot' : 'Create Bot'}
                </h2>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Angry Customer Bot"
                  className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this bot's purpose"
                  className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm"
                />
              </div>

              {/* Guidance */}
              <div>
                <MarkdownEditor
                  label="Guidance"
                  value={formData.guidance}
                  onChange={(val) => setFormData({ ...formData, guidance: val })}
                  placeholder="Describe the bot's personality, scenario, and how it should behave during simulations..."
                  rows={8}
                  helperText="Instructions that define how this bot acts as a simulated customer."
                />
              </div>

              {/* AI Model Provider */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  AI Model Provider
                </label>
                <select
                  value={formData.provider}
                  onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                  className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm bg-white"
                >
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                  <option value="gemini">Gemini</option>
                </select>
              </div>

              {/* AI Model */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  AI Model
                </label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  placeholder="e.g. gpt-4o-mini"
                  className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm"
                />
              </div>

              {/* Temperature & Max Tokens */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Temperature
                  </label>
                  <input
                    type="number"
                    value={formData.temperature}
                    onChange={(e) =>
                      setFormData({ ...formData, temperature: parseFloat(e.target.value) || 0 })
                    }
                    min={0}
                    max={1}
                    step={0.1}
                    className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Max Tokens
                  </label>
                  <input
                    type="number"
                    value={formData.max_tokens}
                    onChange={(e) =>
                      setFormData({ ...formData, max_tokens: parseInt(e.target.value) || 0 })
                    }
                    min={1}
                    className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm"
                  />
                </div>
              </div>

              {/* Status Toggle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, status: 'active' })}
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                      formData.status === 'active'
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full ${
                        formData.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                      }`}
                    />
                    <span
                      className={`text-sm font-medium ${
                        formData.status === 'active' ? 'text-green-700' : 'text-gray-700'
                      }`}
                    >
                      Active
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, status: 'inactive' })}
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                      formData.status === 'inactive'
                        ? 'border-gray-500 bg-gray-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full ${
                        formData.status === 'inactive' ? 'bg-gray-500' : 'bg-gray-400'
                      }`}
                    />
                    <span
                      className={`text-sm font-medium ${
                        formData.status === 'inactive' ? 'text-gray-700' : 'text-gray-700'
                      }`}
                    >
                      Inactive
                    </span>
                  </button>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#1a1a2e]">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving || !formData.name.trim()}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-rose-500 text-white font-semibold rounded-xl hover:bg-rose-600 transition-colors shadow-[3px_3px_0px_#1a1a2e] disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {editingBot ? 'Saving...' : 'Creating...'}
                    </>
                  ) : editingBot ? (
                    'Save Changes'
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Create Bot
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default BotsPage
